import { updateRecord } from '@ms/yammer-data-support';

import {
  FileUploadJobCompletedAction,
  FileUploadJobFailedAction,
  FileUploadJobStartedAction,
} from '../../domains/fileUploadJobs/actions.types';
import {
  PublisherAttachmentAddedAction,
  PublisherAttachmentsRemovedAction,
  PublisherGifPickerClosedAction,
  PublisherGifPickerOpenedAction,
  PublisherInlineAttachmentsChangedAction,
} from '../../domains/publishers/actions.types';
import {
  ResolveWebLinkFulfilledAction,
  ResolveWebLinkPendingAction,
  ResolveWebLinkRejectedAction,
} from '../../domains/resolveWebLink/actions.types';
import { Publishers, isEditPublisher } from '../types';

import {
  addAttachmentToPublisher,
  removeAttachmentsFromPublisher,
  updateAttachmentReference,
  updateInlineAttachmentIds,
} from './attachments';
import { PublishersAction } from './reducer.types';
import { Publisher } from './types';

const openGifPicker = (state: Publishers, { id }: PublisherGifPickerOpenedAction['payload']) =>
  updateRecord(state, id, { isGifPickerOpen: true });

const closeGifPicker = (state: Publishers, { id }: PublisherGifPickerClosedAction['payload']) =>
  updateRecord(state, id, { isGifPickerOpen: false });

const addAttachment = (state: Publishers, payload: PublisherAttachmentAddedAction['payload']) => {
  const { id: publisherId, attachmentReference, isHeaderImage } = payload;

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd: attachmentReference,
    isHeaderImage,
  });
};

const removeAttachments = (state: Publishers, payload: PublisherAttachmentsRemovedAction['payload']) => {
  const { id: publisherId, attachmentReferences } = payload;

  return removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: attachmentReferences,
  });
};

const inlineAttachmentsChanged = (state: Publishers, payload: PublisherInlineAttachmentsChangedAction['payload']) => {
  const { publisherId, inlineAttachmentIds } = payload;

  return updateInlineAttachmentIds({ state, publisherId, inlineAttachmentIds });
};

const addResolvingWeblinkToPublisher = (
  state: Publishers,
  payload: ResolveWebLinkPendingAction['payload']
): Publishers => {
  const { link, publisherId, isAttachmentsEditEnabled, isHeaderImage } = payload;
  const isPublisherEdit = state[publisherId] ? isEditPublisher(state[publisherId]) : false;

  if (isPublisherEdit && !isAttachmentsEditEnabled) {
    return state;
  }

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd: {
      id: link,
      schema: 'resolvingWebLinks',
    },
    isHeaderImage,
  });
};

const removeRejectedWebLinkFromPublisher = (state: Publishers, payload: ResolveWebLinkRejectedAction['payload']) => {
  const { link, publisherId } = payload;

  return removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: [
      {
        id: link,
        schema: 'resolvingWebLinks',
      },
    ],
  });
};

const resolveWebLinkOnPublisher = (
  state: Publishers,
  payload: ResolveWebLinkFulfilledAction['payload']
): Publishers => {
  const {
    link: webLinkUrl,
    publisherId,
    attachedViaMRU,
    isAttachedViaFilePicker,
    isLocked,
    gifType,
    result: { id: webLinkId, schema },
    isAttachmentsEditEnabled,
  } = payload;

  const isPublisherEdit = state[publisherId] ? isEditPublisher(state[publisherId]) : false;

  if (isPublisherEdit && !isAttachmentsEditEnabled) {
    return state;
  }

  return updateAttachmentReference({
    state,
    publisherId,
    attachmentToUpdate: { id: webLinkUrl, schema: 'resolvingWebLinks' },
    updatedAttachment: { id: webLinkId, schema, attachedViaMRU, isAttachedViaFilePicker, isLocked, gifType },
  });
};

const attachFileUploadToPublisher = (state: Publishers, payload: FileUploadJobStartedAction['payload']): Publishers => {
  const { id, publisherId, editorPlaceholderId, isHeaderImage } = payload;

  return addAttachmentToPublisher({
    state,
    publisherId,
    attachmentToAdd: { id, schema: 'fileUploadJobs', editorPlaceholderId },
    isHeaderImage,
  });
};

const promoteFileUploadCompletedOnPublisher = (
  state: Publishers,
  payload: FileUploadJobCompletedAction['payload']
): Publishers => {
  const {
    id,
    publisherId,
    result: { id: resolvedFileId, schema },
    isHeaderImage,
    editorPlaceholderId,
  } = payload;

  return updateAttachmentReference({
    state,
    publisherId,
    attachmentToUpdate: { id, schema: 'fileUploadJobs' },
    updatedAttachment: {
      id: resolvedFileId,
      schema,
      wasAttachedViaUpload: true,
      editorPlaceholderId,
    },
    isHeaderImage,
  });
};

const removeFailedFileUpload = (state: Publishers, payload: FileUploadJobFailedAction['payload']): Publishers => {
  const { id, publisherId } = payload;

  return removeAttachmentsFromPublisher({
    state,
    publisherId,
    attachmentsToRemove: [{ id, schema: 'fileUploadJobs' }],
  });
};

const updateHasRemovedAttachmentsNotDeleted = (
  state: Publishers,
  publisherId: Publisher['id'] | undefined
): Publishers =>
  publisherId ? updateRecord(state, publisherId, { hasRemovedAttachmentsNotDeletedFromSharePoint: true }) : state;

const toggleAttemptedToAttachFileToPublisher = (state: Publishers, publisherId: Publisher['id']) => {
  const publisher = state[publisherId];

  const updatedPublisherFields = {
    hasAttemptedToAttachFile: !publisher.hasAttemptedToAttachFile,
  };

  return updateRecord(state, publisherId, updatedPublisherFields);
};

type AttachmentActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const attachmentActionsReducer: AttachmentActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.GIF_PICKER_OPENED':
      return openGifPicker(state, action.payload);
    case 'PUBLISHER.GIF_PICKER_CLOSED':
      return closeGifPicker(state, action.payload);
    case 'PUBLISHER.ATTACHMENT_ADDED':
      return addAttachment(state, action.payload);
    case 'PUBLISHER.ATTACHMENTS_REMOVED':
      return removeAttachments(state, action.payload);
    case 'PUBLISHER.INLINE_ATTACHMENTS_CHANGED':
      return inlineAttachmentsChanged(state, action.payload);
    case 'WEBLINK.RESOLVE_PENDING':
      return addResolvingWeblinkToPublisher(state, action.payload);
    case 'WEBLINK.RESOLVE_FULFILLED':
      return resolveWebLinkOnPublisher(state, action.payload);
    case 'WEBLINK.RESOLVE_REJECTED':
      return removeRejectedWebLinkFromPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.STARTED':
      return attachFileUploadToPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.COMPLETED':
      return promoteFileUploadCompletedOnPublisher(state, action.payload);
    case 'FILE_UPLOAD_JOB.FAILED':
      return removeFailedFileUpload(state, action.payload);
    case 'PUBLISHER.MARK_EXISTING_FILE_REMOVED':
      return updateRecord(state, action.payload.publisherId, { hasRemovedAttachmentsNotDeletedFromSharePoint: true });
    case 'PUBLISHER.TOGGLE_ATTEMPTED_TO_ATTACH_FILE':
      return toggleAttemptedToAttachFileToPublisher(state, action.payload.publisherId);
    case 'FILE.DELETE_REJECTED':
      return updateHasRemovedAttachmentsNotDeleted(state, action.payload.publisherId);
    default:
      return state;
  }
};
